import { useCallback, useState } from 'react';

import { TableColumn } from 'components/Table';
import { buildTableCsv } from 'utils/tableExport.utils';
import { downloadCsv } from 'utils/csv.utils';

export interface UseTableCsvExportOptions<T extends object> {
  rows: T[];
  columns: TableColumn[];
  filename: string;
  excludeColumnIds?: string[];
  extraColumns?: TableColumn[];
  prepareRows?: (rows: T[]) => Promise<T[]> | T[];
}

export const useTableCsvExport = <T extends object>({
  rows,
  columns,
  filename,
  excludeColumnIds,
  extraColumns = [],
  prepareRows,
}: UseTableCsvExportOptions<T>) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportToCsv = useCallback(async () => {
    setIsExporting(true);

    try {
      const exportRows = prepareRows ? await prepareRows(rows) : rows;
      const csv = buildTableCsv(exportRows, [...columns, ...extraColumns], excludeColumnIds);

      downloadCsv(csv, filename);
    } finally {
      setIsExporting(false);
    }
  }, [columns, excludeColumnIds, extraColumns, filename, prepareRows, rows]);

  return {
    exportToCsv,
    isExporting,
  };
};
